import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useEPMContext } from '../../contexts/EPMContext';
import { useUserContext } from '../../contexts/UserContext';
import { permission } from '../../config/permission';
import Dropdown from "../UI/Dropdown/Dropdown";
import { tagSchema } from './validation/epmSchemas';

/**
 * EpmTagPicker
 *
 * Controlled tag selector that looks like an input box.
 * Reusable across LearningModal (local form state) and TagSection (live save).
 */
const EpmTagPicker = ({ selectedTagIds = [], onChange, disabled = false, id = 'epm-tag-picker' }) => {
    const { tagsMap, createTag } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canCreateTag = checkPermission(permission.XCM_SETTINGS_TAGS_CREATE);

    const [isCreatingTag, setIsCreatingTag] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [isTagLoading, setIsTagLoading] = useState(false);

    const allTags = Object.values(tagsMap || {});
    const currentTags = selectedTagIds.map((tagId) => tagsMap?.[tagId] || { _id: tagId, name: tagId });
    const availableTags = allTags.filter((t) => !selectedTagIds.includes(t._id));

    const dropdownItems = [
        ...(canCreateTag ? [{ _id: 'add_new', val: 'add_new', name: '+ Add new' }] : []),
        ...availableTags.map((t) => ({ _id: t._id, val: t._id, name: t.name })),
    ];

    // ── Add existing tag ──────────────────────────────────────────────────────
    const handleSelectTag = (selectedTagId) => {
        if (!selectedTagId) return;
        if (selectedTagId === 'add_new') {
            setIsCreatingTag(true);
            setNewTagName('');
            return;
        }
        if (!selectedTagIds.includes(selectedTagId)) {
            onChange([...selectedTagIds, selectedTagId]);
        }
    };

    // ── Create & add new tag ──────────────────────────────────────────────────
    const handleCreateNewTag = async () => {
        let formatted;
        try {
            formatted = tagSchema.validateSync(newTagName);
        } catch (validationErr) {
            toast.error(validationErr?.message || 'Invalid tag name.');
            return;
        }

        // Reuse if already exists (case-insensitive)
        const existing = allTags.find((t) => t.name?.toLowerCase() === formatted);
        if (existing) {
            if (selectedTagIds.includes(existing._id)) {
                toast.info(`Tag "${existing.name}" is already added`);
            } else {
                onChange([...selectedTagIds, existing._id]);
                toast.success(`Tag "${existing.name}" added`);
            }
            setIsCreatingTag(false);
            setNewTagName('');
            return;
        }

        setIsTagLoading(true);
        try {
            const newTag = await createTag(formatted);
            if (newTag?._id) {
                onChange([...selectedTagIds, newTag._id]);
                toast.success(`New tag "${formatted}" created and added`);
            }
            setIsCreatingTag(false);
            setNewTagName('');
        } catch (err) {
            toast.error(err?.message || 'Failed to create tag');
        } finally {
            setIsTagLoading(false);
        }
    };

    // ── Remove tag ────────────────────────────────────────────────────────────
    const handleRemoveTag = (tagId) => {
        onChange(selectedTagIds.filter((id) => id !== tagId));
    };

    const isBusy = disabled || isTagLoading;

    return (
        <div className="epm-tag-picker-box">
            {/* Selected tag pills */}
            {currentTags.map((tag) => (
                <span key={tag._id} className="epm-idea__tags">
                    <span>
                        {tag.name}
                        <button
                            type="button"
                            aria-label={`Remove ${tag.name}`}
                            disabled={isBusy}
                            onClick={() => handleRemoveTag(tag._id)}
                        >
                            <X size={12} />
                        </button>
                    </span>
                </span>
            ))}

            {/* Inline create input OR dropdown */}
            {isCreatingTag ? (
                <div className="epm-idea__tag-input">
                    <input
                        type="text"
                        name="tag"
                        placeholder="New tag name..."
                        value={newTagName}
                        autoFocus
                        disabled={isBusy}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); handleCreateNewTag(); }
                            else if (e.key === 'Escape') { setIsCreatingTag(false); setNewTagName(''); }
                        }}
                    />
                    <button
                        type="button"
                        className="add-btn"
                        disabled={isBusy || !newTagName.trim()}
                        onClick={handleCreateNewTag}
                    >
                        Add
                    </button>
                    <button
                        type="button"
                        className="cancel-btn"
                        aria-label="Cancel"
                        disabled={isBusy}
                        onClick={() => { setIsCreatingTag(false); setNewTagName(''); }}
                    >
                        <X size={12} />
                    </button>
                </div>
            ) : (
                <div className="epm-idea__tag-picker">
                    <Dropdown
                        key={selectedTagIds.join('-')}
                        items={dropdownItems}
                        callback={(item) => { if (item?.val) handleSelectTag(item.val); }}
                        noItemSelectedTitle="+ Add tag..."
                        searchable={availableTags.length > 5}
                        width="140"
                        disabled={isBusy}
                        id={id}
                    />
                </div>
            )}
        </div>
    );
};

export default EpmTagPicker;
